import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  TextField, Button, Typography, Box, Paper, IconButton, InputAdornment
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  // Email validation using regex
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Password validation (minimum 8 characters, at least one letter, one number)
  const validatePassword = (password) => {
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@#]{8,}$/;
    return passwordRegex.test(password);
  };

  // Function to handle login API call
  const handleLogin = useCallback(() => {
    const newErrors = {};

    // Validate email
    if (!email) {
      newErrors.email = "Email is required.";
    } else if (!validateEmail(email)) {
      newErrors.email = "Invalid email format.";
    }

    // Validate password
    if (!password) {
      newErrors.password = "Password is required.";
    } else if (!validatePassword(password)) {
      newErrors.password =
        "Password must be at least 8 characters long and contain at least one letter and one number.";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      // Call API for login
      axios.post("https://attendanceapi.gensparc.com/auth/login", {
        email: email,
        password: password
      })
      .then(response => {
        if (response.data.token) {
          localStorage.setItem("token", response.data.token); // Store token
          navigate("/dashboard", { replace: true }); // Redirect to dashboard
        } else {
          alert(response.data.message || "Login failed.");
        }
      })
      .catch(error => {
        console.error("Login Error:", error);
        alert("Invalid email or password.");
      });
    }
  }, [email, password, navigate]);

  // Handling Enter key press for login
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === "Enter") {
        handleLogin();
      }
    };
    window.addEventListener("keypress", handleKeyPress);
    return () => {
      window.removeEventListener("keypress", handleKeyPress);
    };
  }, [handleLogin]);

  return (
    <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="#f5f5f5"
      sx={{ background: "linear-gradient(135deg, #6E8EF8, #A5DCF8)", padding: 0 }}>
      <Typography variant="h3" align="center" gutterBottom sx={{
        color: "#fff", fontWeight: "bold", textShadow: "2px 2px 5px rgba(0, 0, 0, 0.3)"
      }}>
        Welcome to the Attendance App
      </Typography>

      <Paper elevation={6} sx={{
        padding: 4, width: "100%", maxWidth: 400, mt: 2, borderRadius: "12px", backgroundColor: "#fff"
      }}>
        <Typography variant="h5" align="center" gutterBottom>
          Attendance - Login
        </Typography>

        {/* Email Input */}
        <TextField label="Email" variant="outlined" margin="normal" fullWidth
          value={email} onChange={(e) => setEmail(e.target.value)}
          error={!!errors.email} helperText={errors.email}
          sx={{ "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { borderColor: "#1976d2" } } }}
        />

        {/* Password Input */}
        <TextField label="Password" variant="outlined" margin="normal" fullWidth
          type={showPassword ? "text" : "password"}
          value={password} onChange={(e) => setPassword(e.target.value)}
          error={!!errors.password} helperText={errors.password}
          sx={{ "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { borderColor: "#1976d2" } } }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Login Button */}
        <Button variant="contained" color="primary" size="large" onClick={handleLogin} sx={{
          width: "100%", mt: 2, padding: "10px", fontWeight: "bold", fontSize: "16px", borderRadius: "8px",
          "&:hover": { backgroundColor: "#1565c0" }
        }}>
          Login
        </Button>

        {/* Links to Signup and Forgot Password */}
        {/* <Box mt={2} textAlign="center">
          <Typography variant="body2" sx={{ color: "#000", fontWeight: "bold" }}>
            <Link to="/forgot-password" style={{ textDecoration: "none", color: "#1565c0", fontWeight: "bold" }}>
              Forgot Password?
            </Link>
          </Typography>
          <Typography variant="body2" mt={1} sx={{ color: "#000", fontWeight: "bold" }}>
            Don't have an account?{" "}
            <Link to="/signup" style={{ textDecoration: "none", color: "#1565c0", fontWeight: "bold" }}>
              Sign Up
            </Link>
          </Typography>
        </Box> */}
      </Paper>
    </Box>
  );
}

export default Login;
